import { Modal, Form, Input, Select, DatePicker, Row, Col } from 'antd';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { schoolApi } from '../../../api/school.api';
import { clinicApi } from '../../../api/clinic.api';
import { doctorApi } from '../../../api/doctor.api';
import { staffApi } from '../../../api/staff.api';
import dayjs from 'dayjs';

const EventForm = ({ open, onClose, onSubmit, initialValues, loading }) => {
  const [form] = Form.useForm();
  const selectedSchool = Form.useWatch('school', form);
  const selectedClinic = Form.useWatch('clinic', form);

  const { data: schools } = useQuery({ queryKey: ['schools-all'], queryFn: () => schoolApi.getAll({ limit: 200 }), enabled: open });
  const { data: clinics } = useQuery({ queryKey: ['clinics-active'], queryFn: () => clinicApi.getActive(), enabled: open });
  const { data: doctors } = useQuery({
    queryKey: ['doctors-active', selectedClinic],
    queryFn: () => doctorApi.getActive(selectedClinic ? { clinic: selectedClinic } : {}),
    enabled: open && !!selectedClinic,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const { data: staffData } = useQuery({
    queryKey: ['staff-active', selectedSchool],
    queryFn: () => staffApi.getActive({ school: selectedSchool }),
    enabled: open && !!selectedSchool,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          ...initialValues,
          school: initialValues.school?.id || initialValues.school,
          clinic: initialValues.clinic?.id || initialValues.clinic,
          eventDate: initialValues.eventDate ? dayjs(initialValues.eventDate) : null,
          assignedDoctors: (initialValues.assignedDoctors || []).map((d) => d.id || d),
          assignedStaff: (initialValues.assignedStaff || []).map((s) => s.id || s),
        });
      } else { form.resetFields(); }
    }
  }, [open, initialValues, form]);

  return (
    <Modal
      title={initialValues ? 'Edit Event' : 'Create Event'}
      open={open} onCancel={onClose}
      onOk={() => form.validateFields().then((v) => onSubmit({ ...v, eventDate: v.eventDate?.toISOString() }))}
      confirmLoading={loading} width={680} destroyOnClose
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto', paddingRight: 8 } }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="title"
              label="Event Title"
              rules={[
                { required: true, message: 'Event title is required' },
                { max: 200, message: 'Title cannot exceed 200 characters' },
              ]}
            >
              <Input placeholder="Enter event title" maxLength={200} showCount />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ max: 200, message: 'Description cannot exceed 200 characters' }]}
            >
              <Input.TextArea rows={2} placeholder="Event description" maxLength={200} showCount />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="eventDate" label="Event Date & Time" rules={[{ required: true }]}>
              <DatePicker
                style={{ width: '100%' }}
                showTime={{ format: 'HH:mm', showSecond: false }}
                format="DD/MM/YYYY HH:mm"
                popupClassName="wl-event-datepicker"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="location" label="Location" rules={[{ required: true }]}>
              <Input placeholder="Event location" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="school" label="School" rules={[{ required: true }]}>
              <Select
                placeholder="Select school"
                showSearch
                optionFilterProp="children"
                onChange={() => form.setFieldValue('assignedStaff', [])}
              >
                {(schools?.data || []).map((s) => (
                  <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="assignedStaff" label="Assigned Staff">
              <Select
                mode="multiple"
                placeholder={selectedSchool ? 'Select staff' : 'Select a school first'}
                optionFilterProp="children"
                disabled={!selectedSchool}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {(staffData?.data || []).map((s) => (
                  <Select.Option key={s.id} value={s.id}>{s.name} — {s.designation}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="clinic" label="Clinic" rules={[{ required: true }]}>
              <Select
                placeholder="Select clinic"
                showSearch
                optionFilterProp="children"
                onChange={() => form.setFieldValue('assignedDoctors', [])}
              >
                {(clinics?.data || []).map((c) => (
                  <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="assignedDoctors" label="Assigned Doctors">
              <Select
                mode="multiple"
                placeholder={selectedClinic ? 'Select doctors' : 'Select a clinic first'}
                optionFilterProp="children"
                disabled={!selectedClinic}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {(doctors?.data || []).map((d) => (
                  <Select.Option key={d.id} value={d.id}>{d.name} — {d.specialization}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {initialValues && (
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select placeholder="Select status">
                  <Select.Option value="upcoming">Upcoming</Select.Option>
                  <Select.Option value="ongoing">In Progress</Select.Option>
                  <Select.Option value="completed">Completed</Select.Option>
                  <Select.Option value="cancelled">Cancelled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default EventForm;
